import { useState, ReactNode } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper,
} from "@mui/material";

export type ColumnDef<T> = {
  key: string;
  header: string;
  cell: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  sortFn?: (a: T, b: T) => number;
  align?: "left" | "right" | "center";
  width?: string | number;
};

interface SortableTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  rowSx?: (row: T, index: number) => object;
  onRowClick?: (row: T) => void;
  defaultSortKey?: string;
  defaultSortOrder?: "asc" | "desc";
  maxHeight?: string | number;
}

export function SortableTable<T>({
  data,
  columns,
  rowSx,
  onRowClick,
  defaultSortKey,
  defaultSortOrder = "desc",
  maxHeight,
}: SortableTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(defaultSortOrder);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortOrder("desc");
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortKey) return 0;
    const column = columns.find((c) => c.key === sortKey);
    if (!column || !column.sortFn) return 0;
    const result = column.sortFn(a, b);
    return sortOrder === "asc" ? result : -result;
  });

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight }}>
      <Table size="small" stickyHeader={!!maxHeight}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align ?? "left"} sx={{ width: col.width, fontWeight: 600 }}>
                {col.sortable ? (
                  <TableSortLabel
                    active={sortKey === col.key}
                    direction={sortKey === col.key ? sortOrder : "asc"}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.header}
                  </TableSortLabel>
                ) : (
                  col.header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row, index) => (
            <TableRow
              key={index}
              hover={!!onRowClick}
              onClick={() => onRowClick?.(row)}
              sx={{ cursor: onRowClick ? "pointer" : undefined, ...(rowSx?.(row, index) ?? {}) }}
            >
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align ?? "left"}>
                  {col.cell(row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {sortedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                No results found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
